/**
 * ProximateObjectivesTree — Cascading Objective Hierarchy
 *
 * Renders proximate objectives in a recursive tree.
 * Each level inherits from its parent, narrowing the objective down to
 * the specific team/division level.
 *
 * Supports:
 *   - Inline "Add child" at each node
 *   - Status badge + due date display
 *   - Org unit label
 *   - Edit/delete per node
 */

import { useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PencilIcon,
  BuildingOfficeIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useStrategyKernelStore } from '../../../stores/prioritization/strategyKernelStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import type { ProximateObjective } from '../../../types/prioritization/strategyKernel';
import { getProximateObjectiveStatusLabel } from '../../../types/prioritization/strategyKernel';

// ============================================================================
// Status badge colors
// ============================================================================

const STATUS_COLORS: Record<ProximateObjective['status'], string> = {
  draft: 'bg-[var(--bg-raised)] text-[var(--text-ter)]',
  active: 'bg-[var(--slate)]/40 text-[var(--text-ter)]',
  achieved: 'bg-[var(--green)]/40 text-[var(--green)] dark:text-[var(--text-ter)]',
  deferred: 'bg-[var(--mustard)]/40 text-[var(--mustard)]',
};

// ============================================================================
// Level colors (visual indentation cue)
// ============================================================================

const LEVEL_COLORS = [
  'border-l-blue-500',
  'border-l-purple-500',
  'border-l-green-500',
  'border-l-orange-500',
  'border-l-pink-500',
];

// ============================================================================
// Inline edit form for a single objective
// ============================================================================

interface InlineEditFormProps {
  initial: Partial<ProximateObjective>;
  onSave: (data: { title: string; description: string; dueDate: string; status: ProximateObjective['status'] }) => void;
  onCancel: () => void;
}

function InlineEditForm({ initial, onSave, onCancel }: InlineEditFormProps) {
  const [title, setTitle] = useState(initial.title ?? '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [dueDate, setDueDate] = useState(initial.dueDate ?? '');
  const [status, setStatus] = useState<ProximateObjective['status']>(initial.status ?? 'draft');

  const handleSave = () => {
    if (!title.trim()) return;
    onSave({ title: title.trim(), description: description.trim(), dueDate, status });
  };

  return (
    <div className="space-y-2 p-3 bg-[var(--bg-raised)] border border-[var(--rule)]">
      <input
        type="text"
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Objective title (what needs to be true?)"
        className="w-full text-sm border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-2 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600"
        autoFocus
      />
      <textarea
        value={description}
        onChange={e => setDescription(e.target.value)}
        placeholder="Brief description (optional)"
        rows={2}
        className="w-full text-xs border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)] placeholder-neutral-600 resize-none"
      />
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="block text-xs text-[var(--text-ter)] mb-1">Due date</label>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="w-full text-xs border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)]"
          />
        </div>
        <div className="flex-1">
          <label className="block text-xs text-[var(--text-ter)] mb-1">Status</label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as ProximateObjective['status'])}
            className="w-full text-xs border border-[var(--rule)] px-2.5 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 bg-[var(--bg-raised)] text-[var(--text-ter)]"
          >
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="achieved">Achieved</option>
            <option value="deferred">Deferred</option>
          </select>
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] px-2 py-1">
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!title.trim()}
          className="text-xs bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 font-medium"
        >
          Save
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Recursive node
// ============================================================================

interface ObjectiveNodeProps {
  objective: ProximateObjective;
  kernelId: string;
  depth: number;
  allObjectives: ProximateObjective[];
}

function ObjectiveNode({ objective, kernelId, depth, allObjectives }: ObjectiveNodeProps) {
  const { updateObjective, deleteObjective, createObjective } = useStrategyKernelStore();
  const addNotification = useUIStore((s) => s.addNotification);
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addingChild, setAddingChild] = useState(false);

  const hasChildren = (objective.children?.length ?? 0) > 0;
  const levelColor = LEVEL_COLORS[depth % LEVEL_COLORS.length];

  const handleUpdate = useCallback(
    async (data: { title: string; description: string; dueDate: string; status: ProximateObjective['status'] }) => {
      await updateObjective(objective.id, {
        title: data.title,
        description: data.description || undefined,
        dueDate: data.dueDate || undefined,
        status: data.status,
      });
      setEditing(false);
    },
    [objective.id, updateObjective]
  );

  const handleAddChild = useCallback(
    async (data: { title: string; description: string; dueDate: string; status: ProximateObjective['status'] }) => {
      const result = await createObjective({
        kernelId,
        parentId: objective.id,
        title: data.title,
        description: data.description || undefined,
        dueDate: data.dueDate || undefined,
        level: depth + 1,
      });
      if (!result.ok) {
        // Keep the form open with what was typed so the user can retry.
        addNotification({
          type: 'error',
          title: 'Objective was not created',
          message: result.error ?? 'Try again.',
          duration: 6000,
        });
        return;
      }
      setAddingChild(false);
    },
    [kernelId, objective.id, depth, createObjective, addNotification]
  );

  const handleDelete = useCallback(async () => {
    if (hasChildren) {
      const confirmed = window.confirm('This will also remove all child objectives. Continue?');
      if (!confirmed) return;
    }
    await deleteObjective(objective.id);
  }, [objective.id, hasChildren, deleteObjective]);

  // Suppress unused import warning
  void allObjectives;

  return (
    <div className={`border-l-2 ${levelColor} pl-3 space-y-1`} style={{ marginLeft: depth > 0 ? '12px' : '0' }}>
      {/* Node header */}
      {editing ? (
        <InlineEditForm
          initial={objective}
          onSave={handleUpdate}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div className="group flex items-start gap-2 bg-[var(--bg-raised)] border border-[var(--rule)] px-3 py-2.5 hover:border-[var(--rule)] transition-colors">
          {/* Expand/collapse toggle */}
          <button
            onClick={() => setExpanded(v => !v)}
            className={`mt-0.5 shrink-0 text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors ${!hasChildren && 'invisible'}`}
          >
            {expanded ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : (
              <ChevronRightIcon className="w-3.5 h-3.5" />
            )}
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[var(--text-ter)]">{objective.title}</span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[objective.status]}`}>
                {getProximateObjectiveStatusLabel(objective.status)}
              </span>
            </div>
            {objective.description && (
              <p className="text-xs text-[var(--text-ter)] mt-0.5">{objective.description}</p>
            )}
            <div className="flex items-center gap-3 mt-1 text-xs text-[var(--text-ter)]">
              {objective.orgUnitName && (
                <span className="flex items-center gap-1">
                  <BuildingOfficeIcon className="w-3 h-3" />
                  {objective.orgUnitName}
                </span>
              )}
              {objective.dueDate && (
                <span className="flex items-center gap-1">
                  <CalendarIcon className="w-3 h-3" />
                  {new Date(objective.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
              <span className="text-[var(--text-sec)]">Level {objective.level}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setAddingChild(v => !v)}
              className="text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors"
              title="Add child objective"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => setEditing(true)}
              className="text-[var(--text-sec)] hover:text-[var(--text-ter)] transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleDelete}
              className="text-[var(--text-sec)] hover:text-[var(--coral)] transition-colors"
              title="Delete"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Add child form */}
      {addingChild && (
        <div className="ml-4">
          <InlineEditForm
            initial={{ level: depth + 1 }}
            onSave={handleAddChild}
            onCancel={() => setAddingChild(false)}
          />
        </div>
      )}

      {/* Children */}
      {expanded && (objective.children ?? []).map(child => (
        <ObjectiveNode
          key={child.id}
          objective={child}
          kernelId={kernelId}
          depth={depth + 1}
          allObjectives={[]}
        />
      ))}
    </div>
  );
}

// ============================================================================
// Top-level component
// ============================================================================

interface ProximateObjectivesTreeProps {
  kernelId: string;
}

export function ProximateObjectivesTree({ kernelId }: ProximateObjectivesTreeProps) {
  const { getObjectivesTree, getObjectivesForKernel, createObjective } = useStrategyKernelStore();
  const addNotification = useUIStore((s) => s.addNotification);
  const [addingRoot, setAddingRoot] = useState(false);

  const tree = getObjectivesTree(kernelId);
  const allObjectives = getObjectivesForKernel(kernelId);

  const handleAddRoot = useCallback(
    async (data: { title: string; description: string; dueDate: string; status: ProximateObjective['status'] }) => {
      const result = await createObjective({
        kernelId,
        title: data.title,
        description: data.description || undefined,
        dueDate: data.dueDate || undefined,
        level: 0,
      });
      if (!result.ok) {
        // Keep the form open with what was typed so the user can retry.
        addNotification({
          type: 'error',
          title: 'Objective was not created',
          message: result.error ?? 'Try again.',
          duration: 6000,
        });
        return;
      }
      setAddingRoot(false);
    },
    [kernelId, createObjective, addNotification]
  );

  return (
    <div className="space-y-3">
      {/* Instructions */}
      <div className="text-sm text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)] px-4 py-3">
        <p className="font-medium text-[var(--text-ter)] mb-1">Proximate Objectives</p>
        <p>Set targets close enough at hand that the organization can reasonably be expected to hit them. Start with the enterprise-level objective, then cascade down to divisions and teams using "Add child" on each node.</p>
      </div>

      {/* Tree */}
      <div className="space-y-2">
        {tree.map(node => (
          <ObjectiveNode
            key={node.id}
            objective={node}
            kernelId={kernelId}
            depth={0}
            allObjectives={allObjectives}
          />
        ))}
      </div>

      {/* Add root objective */}
      {addingRoot ? (
        <InlineEditForm
          initial={{ level: 0 }}
          onSave={handleAddRoot}
          onCancel={() => setAddingRoot(false)}
        />
      ) : (
        <button
          onClick={() => setAddingRoot(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)] hover:bg-[var(--slate)]/20 px-3 py-1.5 border border-[var(--slate)] transition-colors"
        >
          <PlusIcon className="w-4 h-4" />
          {tree.length === 0 ? 'Add enterprise-level objective' : 'Add another top-level objective'}
        </button>
      )}

      {tree.length === 0 && !addingRoot && (
        <p className="text-sm text-[var(--text-ter)] italic">
          No objectives yet. Add your first enterprise-level objective to start the cascade.
        </p>
      )}
    </div>
  );
}
